/**
 * Locale-aware CIVIL-DATE formatting for the billing surfaces.
 *
 * Two rules, one mechanism:
 *
 *  - The PLATFORM SHELF (the platform-admin console, `/billing/platform/*`)
 *    renders every date in Malaysia format (DD/MM/YYYY), regardless of any
 *    tenant's currency — it is operator-facing, and the operator is one team.
 *  - The customer-facing MONEY screens (Budget, Spending, Billing) render dates
 *    in the locale of the DISPLAY CURRENCY they show money in, so the date order
 *    matches the numbers beside it: a USD tenant sees M/D/YYYY, a MYR tenant
 *    sees D/M/YYYY.
 *
 * ── Why there is no currency→locale MAP to maintain ──────────────────────
 *
 * ISO-4217 assigns each NATIONAL currency a three-letter code whose first two
 * letters ARE the ISO-3166 country code (USD→US, MYR→MY, GBP→GB, JPY→JP, …).
 * So the locale is simply `en-${code.slice(0, 2)}`: `Intl` derives the
 * date-part ORDER from the region, while the `en-` language keeps month names
 * English. Adding a currency needs no change here.
 *
 * Supranational codes (EUR, and the `X…` metals/funds) name no country, so the
 * region is not a real ISO-3166 one; `Intl` ignores it and falls back to the
 * plain `en` pattern (M/D/YYYY), which is the correct neutral default. That is
 * an accepted edge — this deployment charges in MYR and shows USD as the
 * canonical alternative, and both resolve exactly.
 *
 * ── UTC vs LOCAL — this comes in a PAIR, and the value decides which ──────
 *
 * Two kinds of value flow through the billing UI and they render differently:
 *
 *  - a midnight-UTC CIVIL DAY (a subscription term's `effectiveFrom`, a
 *    "suggested next due" day — anything written as `…T00:00:00Z`). Rendering
 *    this in the viewer's zone shifts the shown day one west of the dateline,
 *    so the `…Date` helpers pin `timeZone: 'UTC'`.
 *  - a wall-clock INSTANT (a Stripe period/renewal boundary, an invoice date, a
 *    `paid_through` stamped at `23:59:59` LOCAL, a `created_at`). Pinning THESE
 *    to UTC is the mirror bug — it moves a real timestamp's calendar day for a
 *    viewer near a day boundary — so the `…DateLocal` helpers do NOT pin a zone.
 *
 * This mirrors the two per-file helpers these replace (`formatDateUTC` for the
 * civil day, `formatDate`/`toLocaleDateString()` local for the instant); the
 * one-helper-fits-all first cut regressed every instant to its UTC day. All are
 * display-only: an `<input type="date">` value must stay `YYYY-MM-DD` and has
 * its own ISO helper at the call site.
 */

/** The platform shelf's fixed date locale (Malaysia → DD/MM/YYYY). */
export const PLATFORM_DATE_LOCALE = 'en-MY'

/**
 * BCP-47 locale whose date-part ORDER matches a currency's country, derived
 * from the ISO-4217 ↔ ISO-3166 convention above. Falls back to `en-US` for a
 * missing or malformed code — the canonical currency is USD, so US order is the
 * safe default when the currency is unknown.
 *
 * SUPRANATIONAL codes are the one class the convention does not cover: EUR's
 * pseudo-region `EU` and the `X…` metals/funds codes name no country, so `Intl`
 * would fall back to plain `en` (M/D/YYYY). The Eurozone — and every locale that
 * quotes an X-code — writes D/M/YYYY, so these map to `en-GB` (English, D/M)
 * rather than reading as US order. This is a rule for the whole supranational
 * class, not a per-currency map.
 */
export function currencyDateLocale(currency: string | null | undefined): string {
  const code = (currency ?? '').toUpperCase()
  if (!/^[A-Z]{3}$/.test(code)) return 'en-US'
  const region = code.slice(0, 2)
  if (region === 'EU' || code[0] === 'X') return 'en-GB'
  return `en-${region}`
}

function formatDateParts(
  iso: string | null | undefined,
  locale: string,
  placeholder: string,
  utc: boolean,
): string {
  if (!iso) return placeholder
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return placeholder
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(utc ? { timeZone: 'UTC' } : {}),
  }).format(d)
}

// The four public helpers below are the two surfaces (PLATFORM shelf / money
// screen, differing only in locale) × the two value kinds (UTC civil day /
// LOCAL wall-clock instant) documented in the file header. Pick the `…Local`
// variant for anything that is a real timestamp rather than a `…T00:00:00Z`
// civil day.

/** PLATFORM SHELF, midnight-UTC CIVIL DAY → Malaysia order, rendered in UTC. */
export function formatPlatformDate(
  iso: string | null | undefined,
  placeholder = '—',
): string {
  return formatDateParts(iso, PLATFORM_DATE_LOCALE, placeholder, true)
}

/** PLATFORM SHELF, wall-clock INSTANT → Malaysia order, rendered in LOCAL time. */
export function formatPlatformDateLocal(
  iso: string | null | undefined,
  placeholder = '—',
): string {
  return formatDateParts(iso, PLATFORM_DATE_LOCALE, placeholder, false)
}

/** MONEY SCREEN, midnight-UTC CIVIL DAY → display-currency order, in UTC. */
export function formatCurrencyDate(
  iso: string | null | undefined,
  currency: string | null | undefined,
  placeholder = '—',
): string {
  return formatDateParts(iso, currencyDateLocale(currency), placeholder, true)
}

/** MONEY SCREEN, wall-clock INSTANT → display-currency order, in LOCAL time. */
export function formatCurrencyDateLocal(
  iso: string | null | undefined,
  currency: string | null | undefined,
  placeholder = '—',
): string {
  return formatDateParts(iso, currencyDateLocale(currency), placeholder, false)
}
